import { ApiProvider, HorseNamingRequest, HorseNamingResponse, HorseNamingApiResponse, UsageStatsResponse } from '../types/api';

const API_KEYS: Record<ApiProvider, string> = {
  'Anthropic': process.env.REACT_APP_HORSENAMING_ANTHROPIC_API_KEY || '',
  'OpenAI': process.env.REACT_APP_HORSENAMING_OPENAI_API_KEY || '',
  'OpenRouter': process.env.REACT_APP_HORSENAMING_OPENROUTER_API_KEY || ''
};

class HorseNamingApiClient {
  private baseURL = process.env.REACT_APP_HORSENAMING_BASE_URL || 'https://ha2.stallionmatch.ai';

  private async request<T = any>(
    endpoint: string,
    apiProvider: ApiProvider,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}/api${endpoint}`;
    const apiKey = API_KEYS[apiProvider];

    console.log(`Horse Naming API Debug - Provider: ${apiProvider}, API Key present: ${!!apiKey}, Key length: ${apiKey?.length || 0}`);

    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': apiKey,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}`;

        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }

        throw new Error(errorMessage);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }

      return await response.text() as unknown as T;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw new Error('Network error - please check your connection');
      }

      throw new Error(error instanceof Error ? error.message : 'Unknown error occurred');
    }
  }

  async generateNames(
    request: HorseNamingRequest,
    apiProvider: ApiProvider
  ): Promise<HorseNamingResponse> {
    try {
      const response = await this.request<HorseNamingApiResponse>('/horseNaming', apiProvider, {
        method: 'POST',
        body: JSON.stringify(request),
      });

      // Check if response has the expected format
      if (response.suggestions && Array.isArray(response.suggestions)) {
        return {
          success: true,
          suggestions: response.suggestions
        };
      } else {
        return {
          success: false,
          error: 'Unexpected response format from API'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to generate names'
      };
    }
  }

  async getUsageStats(): Promise<UsageStatsResponse> {
    try {
      const usageApiKey = process.env.REACT_APP_USAGE_STATISTICS_API_KEY;

      if (!usageApiKey) {
        throw new Error('Usage statistics API key not configured');
      }

      const usageApiUrl = process.env.REACT_APP_HORSENAMING_BASE_URL || 'https://ha2.stallionmatch.ai';
      const response = await fetch(`${usageApiUrl}/admin/sources/usage`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-USAGE-API-KEY': usageApiKey,
        },
        mode: 'cors',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw new Error(`Cannot connect to usage statistics API at ${process.env.REACT_APP_HORSENAMING_BASE_URL || 'https://ha2.stallionmatch.ai'}. Please ensure the API server is running.`);
      }
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch usage statistics');
    }
  }
}

export const horseNamingService = new HorseNamingApiClient();